/* ==========================================================================
   Kopstorm - Automatic free gift
   kopstorm-free-gift.js

   Keeps the free gift lines in the cart in step with the qualifying products,
   whoever changed the cart: the theme buy buttons, Upcart, Kaching, a landing
   page, or the cart page itself.

   Design notes
   ------------
   1. One reconciliation function, driven by the cart rather than by the click.
      Every trigger below simply asks for a reconciliation, so add, quantity
      change, removal and a restored session all take the same path and the
      result is idempotent.
   2. The write is a single POST to /cart/update.js keyed by variant id, which
      adds, adjusts and removes in one request. In practice the head hook has
      already folded the gift into the shopper's own add or quantity change,
      so this module mostly confirms the cart is right and writes nothing.
   3. Four layers of trigger, because no single one covers every app:
        a. the early hook in the head, which sees any cart write made through
           fetch or XMLHttpRequest, including Upcart's own;
        b. Upcart's public subscriptions, which are the fast path when the app
           is live and which also fire when Upcart owns the add to cart;
        c. a click fallback, which reconciles shortly after any click that
           could have been an add to cart, so the gift still lands even if an
           app performs its write in a way nothing else observes;
        d. page load, focus and pageshow.
      Duplicate triggers are harmless: the reconciliation only writes when the
      cart is actually wrong.
   4. Nothing is written for a gift the shopper removed by hand, unless the
      entitlement grows afterwards. Without that rule the gift reappears the
      instant it is deleted, which reads as a broken cart.
   ========================================================================== */
(function () {
  'use strict';

  var DATA = window.KopstormFreeGiftData;
  if (!DATA || !DATA.enabled) return;

  var DEBUG = DATA.designMode || /[?&]kopfg=debug/.test(window.location.search);
  try {
    if (/[?&]kopfg=debug/.test(window.location.search)) sessionStorage.setItem('kopstorm:freegift:debug', '1');
    if (sessionStorage.getItem('kopstorm:freegift:debug') === '1') DEBUG = true;
  } catch (e) { /* private mode */ }

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Kopstorm free gift]');
    console.log.apply(console, args);
  }

  /* A misconfigured rule must be loud on the storefront, not only in the
     theme editor. The person checking is on a preview URL, not in the editor. */
  if (DATA.issues && DATA.issues.length) {
    DATA.issues.forEach(function (issue) { console.warn('[Kopstorm free gift] ' + issue); });
  }
  if (!DATA.rules || !DATA.rules.length) {
    console.warn('[Kopstorm free gift] No usable rule, so no gift can be added. Run KopstormFreeGift.diagnose() for the reason.');
    window.KopstormFreeGift = {
      data: DATA,
      diagnose: function () {
        console.log('[Kopstorm free gift] Feature is on but no rule is usable.');
        (DATA.issues || []).forEach(function (issue) { console.log('  - ' + issue); });
        if (!DATA.issues || !DATA.issues.length) {
          console.log('  - No collection and no gift product are picked. Theme settings, Kopstorm free gift.');
        }
        return { rules: 0, issues: DATA.issues || [] };
      }
    };
    return;
  }

  /* A gift that is not priced 0 costs the shopper money until a discount
     covers it. That is a deliberate mode, but it must never be quiet. */
  var priced = (DATA.rules || []).filter(function (rule) { return rule.price > 0; });
  if (priced.length) {
    priced.forEach(function (rule) {
      console.warn('[Kopstorm free gift] "' + rule.title + '" is added at ' + (rule.price / 100).toFixed(2) +
        ', not at 0. The theme does not discount it. Without a matching automatic discount the shopper pays for the gift.');
    });
  }

  var ROUTES = DATA.routes || {};
  var ROOT = (ROUTES.root === '/' ? '' : (ROUTES.root || ''));
  var CART_URL = (ROUTES.cart || '/cart') + '.js';
  var UPDATE_URL = ROUTES.update || '/cart/update.js';

  /* The early hook kept a native fetch reference before the apps loaded. Using
     it for our own writes means our requests cannot be swallowed or rewritten
     by an app that wrapped fetch afterwards. */
  var HOOK = window.__kopFgHook || {};
  var rawFetch = HOOK.fetch || window.fetch;

  var MAX_PASSES = 60;
  var passes = 0;
  var busy = false;
  var timer = null;
  var lastCart = null;
  var deadVariants = {};

  var GIFT = {};
  (DATA.giftVariants || []).forEach(function (id) { GIFT[id] = true; });
  var GIFT_PRODUCT = {};
  (DATA.giftProducts || []).forEach(function (id) { GIFT_PRODUCT[id] = true; });

  /* Last match wins, so rule 1 is the general case and rules 2 and 3 are the
     exceptions that override it. */
  var QUALIFY = {};
  var ALL_RULE = null;
  function indexRules() {
    QUALIFY = {};
    ALL_RULE = null;
    DATA.rules.forEach(function (rule, index) {
      if (!rule.variants) rule.variants = [];
      (rule.products || []).forEach(function (pid) { QUALIFY[pid] = index; });
      if (rule.all) ALL_RULE = index;
    });

    /* Hand the refreshed variant map back to the head hook, so an add made
       after a collection top up still carries its gift on the same click. */
    var hook = window.__kopFgHook;
    if (!hook) return;
    var map = {};
    DATA.rules.forEach(function (rule) {
      (rule.variants || []).forEach(function (vid) { map[vid] = rule.variant; });
    });
    hook.giftFor = map;
  }
  indexRules();

  /* ---------------------------------------------------------------------- */
  /* Collections over 50 products                                            */
  /* ---------------------------------------------------------------------- */
  /* The head hook starts this before the page has finished parsing, so an add
     on the very first click is not judged against a truncated map. All that is
     left here is to wait for it and re-index. */
  function topUpCollections() {
    var hook = window.__kopFgHook;
    var pending = hook && hook.pending;
    return Promise.resolve(pending).then(indexRules, indexRules);
  }

  /* ---------------------------------------------------------------------- */
  /* Session memory: what we granted, and what the shopper removed by hand   */
  /* ---------------------------------------------------------------------- */
  var STORE_KEY = 'kopstorm:freegift';

  function readStore() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || { granted: {}, declined: {} };
    } catch (e) {
      return { granted: {}, declined: {} };
    }
  }

  function writeStore(state) {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  }

  var store = readStore();

  /* ---------------------------------------------------------------------- */
  /* The maths                                                              */
  /* ---------------------------------------------------------------------- */
  function entitlement(items) {
    var units = {};

    items.forEach(function (line) {
      if (GIFT[line.variant_id] || GIFT_PRODUCT[line.product_id]) return;
      var index = QUALIFY[line.product_id];
      if (index === undefined) index = ALL_RULE;
      if (index === undefined || index === null) return;
      units[index] = (units[index] || 0) + (line.quantity || 0);
    });

    var want = {};
    Object.keys(units).forEach(function (index) {
      var rule = DATA.rules[index];
      if (!rule || deadVariants[rule.variant]) return;
      var qty = DATA.mode === 'cart' ? 1 : units[index];
      want[rule.variant] = (want[rule.variant] || 0) + qty;
    });

    Object.keys(want).forEach(function (variant) {
      if (want[variant] > DATA.max) want[variant] = DATA.max;
    });

    return want;
  }

  function held(items) {
    var have = {};
    items.forEach(function (line) {
      if (!GIFT[line.variant_id]) return;
      have[line.variant_id] = (have[line.variant_id] || 0) + (line.quantity || 0);
    });
    return have;
  }

  function differs(items) {
    var want = entitlement(items);
    var have = held(items);
    return Object.keys(want).concat(Object.keys(have)).some(function (variant) {
      return (want[variant] || 0) !== (have[variant] || 0);
    });
  }

  function plan(items) {
    var want = entitlement(items);
    var have = held(items);
    var updates = {};
    var seen = {};

    Object.keys(want).concat(Object.keys(have)).forEach(function (variant) {
      if (seen[variant]) return;
      seen[variant] = true;

      var target = want[variant] || 0;
      var current = have[variant] || 0;

      if (target === current) {
        if (target > 0) store.granted[variant] = target;
        return;
      }

      if (target < current) {
        /* Always pull an over quantity back down. This is the exploit guard. */
        updates[variant] = target;
        if (target === 0) delete store.granted[variant];
        else store.granted[variant] = target;
        return;
      }

      if (DATA.respectRemoval) {
        var granted = store.granted[variant] || 0;
        if (current === 0 && granted > 0 && target <= granted) {
          store.declined[variant] = 1;
          log('gift', variant, 'was removed by hand, leaving it out');
          return;
        }
        if (store.declined[variant] && target <= (store.granted[variant] || 0)) return;
        if (current > 0 && target <= granted) return;
      }

      delete store.declined[variant];
      updates[variant] = target;
      store.granted[variant] = target;
    });

    writeStore(store);
    return updates;
  }

  /* ---------------------------------------------------------------------- */
  /* Cart access                                                            */
  /* ---------------------------------------------------------------------- */
  function fetchCart() {
    return rawFetch(CART_URL, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'X-Kopstorm-Free-Gift': '1' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) { return response.json(); });
  }

  /* The Concept theme re-renders its cart drawer, cart page and header count
     only when a cartUpdate payload carries the rendered sections for them
     (cart.js onCartUpdate returns early when cart.sections is missing). Ask the
     theme which sections it wants, the same way its own cart-items does, and
     let Shopify render them in the update response, so a gift line shows in
     the theme drawer the moment it is written and not on the next reload. */
  function bundledSections() {
    var sections = [];
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
      );
    } catch (e) { /* older browsers */ }
    return sections.filter(function (id, index) { return id && sections.indexOf(id) === index; });
  }

  function write(updates) {
    var body = { updates: updates };
    var sections = bundledSections();
    if (sections.length) body.sections = sections;
    return rawFetch(UPDATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Kopstorm-Free-Gift': '1'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok) {
          var error = new Error(payload && (payload.description || payload.message) || 'cart update failed');
          error.payload = payload;
          error.status = response.status;
          throw error;
        }
        return payload;
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Telling the rest of the page                                           */
  /* ---------------------------------------------------------------------- */
  /* The head hook rewrites adds and quantity changes on its own, so it needs
     the same picture of the cart this module has: what each line holds, which
     gifts were granted and which the shopper declined. */
  function syncHook(cart) {
    var hook = window.__kopFgHook;
    if (!hook) return;
    var have = {};
    var lines = {};
    ((cart && cart.items) || []).forEach(function (line) {
      if (GIFT[line.variant_id]) have[line.variant_id] = (have[line.variant_id] || 0) + line.quantity;
      if (line.key) lines[line.key] = { vid: line.variant_id, pid: line.product_id, q: line.quantity };
    });
    hook.have = have;
    hook.lines = lines;
    hook.blocked = store.declined || {};
    hook.granted = store.granted || {};
    hook.dead = deadVariants;
  }

  /* One redraw per write. The theme's cart-items listens for a cartUpdate
     that carries rendered sections and redraws the drawer, cart page and
     header count from it, then dispatches cart:updated itself. Dispatching
     cart:refresh on top of that made the drawer fetch and repaint itself a
     second time, a beat later, which is the flicker. cart:refresh is only
     used when the write came back without sections. */
  function announce(cart) {
    lastCart = cart;
    syncHook(cart);

    if (typeof window.upcartRefreshCart === 'function') {
      try { window.upcartRefreshCart(); } catch (e) { log('upcartRefreshCart failed', e); }
    }

    document.querySelectorAll('cart-count').forEach(function (node) {
      node.textContent = cart.item_count;
      if (cart.item_count > 0) node.removeAttribute('hidden');
    });

    var pubsub = window.theme && window.theme.pubsub;
    if (cart.sections && pubsub && pubsub.publish && pubsub.PUB_SUB_EVENTS) {
      try {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kopstorm-free-gift', cart: cart });
        return;
      } catch (e) { /* theme not ready, fall through */ }
    }

    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, open: false } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    } catch (e) { /* older browsers */ }
  }

  /* ---------------------------------------------------------------------- */
  /* Reconciliation                                                         */
  /* ---------------------------------------------------------------------- */
  /* No cart write of ours may ever overlap another cart write. Two writes on
     a session with no cart token yet make Shopify mint two carts and one of
     them is thrown away, which empties the drawer. */
  function writeInFlight() {
    var hook = window.__kopFgHook;
    return !!(hook && hook.writing);
  }

  function reconcile(cart) {
    if (busy || writeInFlight()) return Promise.resolve(null);
    if (passes >= MAX_PASSES) return Promise.resolve(null);
    passes += 1;
    busy = true;

    var source = cart ? Promise.resolve(cart) : fetchCart();

    return source
      .then(function (current) {
        lastCart = current;
        syncHook(current);
        var updates = plan(current.items || []);
        if (!Object.keys(updates).length) { log('cart already correct'); return null; }
        log('writing', updates);
        return write(updates).then(function (next) {
          announce(next);
          paint();
          log('cart written, gift lines now', held(next.items || []));
          return next;
        });
      })
      .catch(function (error) {
        /* A rejected gift line, usually sold out. Stand the rule down for this
           visit rather than retrying on every cart change. */
        if (error && error.status === 422) {
          Object.keys(GIFT).forEach(function (variant) { deadVariants[variant] = 1; });
          if (window.__kopFgHook) window.__kopFgHook.dead = deadVariants;
          console.warn('[Kopstorm free gift] Shopify refused the gift line, so the gift is off for this visit. Usually the zero priced variant is out of stock. Set it to continue selling when out of stock.', error.payload || error);
        } else {
          log('reconcile failed', error);
        }
        return null;
      })
      .then(function (result) {
        busy = false;
        return result;
      });
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(function () { reconcile(); }, delay || 140);
  }

  /* ---------------------------------------------------------------------- */
  /* FREE label                                                             */
  /* ---------------------------------------------------------------------- */
  var ZERO = /^[^\d]*0[.,]0{2}[^\d]*$/;

  function isZeroText(text) {
    var value = (text || '').replace(/\s|&nbsp;/g, '').trim();
    if (!value) return false;
    if (DATA.zeroMoney && value === DATA.zeroMoney.replace(/\s/g, '')) return true;
    return ZERO.test(value);
  }

  function giftRows(root) {
    var rows = [];
    if (!root || !root.querySelectorAll) return rows;

    DATA.giftVariants.forEach(function (id) {
      root.querySelectorAll('[data-variant-id="' + id + '"]').forEach(function (node) { rows.push(node); });
    });

    if (lastCart && lastCart.items) {
      lastCart.items.forEach(function (line) {
        if (!GIFT[line.variant_id] || !line.key) return;
        var safe = window.CSS && CSS.escape ? CSS.escape(line.key) : line.key.replace(/(["\\])/g, '\\$1');
        var node = root.querySelector('[id="' + safe + '"]') || root.querySelector('[data-key="' + safe + '"]');
        if (node && rows.indexOf(node) === -1) rows.push(node);
      });
    }

    return rows;
  }

  function relabel(root) {
    giftRows(root).forEach(function (row) {
      row.querySelectorAll('*').forEach(function (node) {
        if (node.children.length) return;
        if (node.dataset && node.dataset.kopFgDone === '1') return;
        if (!isZeroText(node.textContent)) return;
        if (node.dataset) node.dataset.kopFgDone = '1';
        node.textContent = DATA.freeLabel;
        node.classList.add('kop-fg-free');
      });
    });
  }

  /* Page styles cannot cross a shadow boundary. When Upcart is rendering
     inside one, the same rules the theme printed in the head are copied into
     that root, once. Nothing else in the lock is JavaScript. */
  function shadowLock() {
    if (!DATA.lockLine) return;
    var root = window.upcartDocumentOrShadowRoot;
    if (!root || !root.host || !root.appendChild) return;
    if (root.querySelector('style[data-kop-fg-lock]')) return;
    var source = document.querySelector('style[data-kop-fg-lock]');
    if (!source) return;
    var style = document.createElement('style');
    style.setAttribute('data-kop-fg-lock', '');
    style.textContent = source.textContent;
    root.appendChild(style);
    log('lock styles copied into the Upcart shadow root');
  }

  function paint() {
    shadowLock();
    if (!DATA.labelFree) return;
    relabel(document);
    if (window.upcartDocumentOrShadowRoot) relabel(window.upcartDocumentOrShadowRoot);
  }

  /* ---------------------------------------------------------------------- */
  /* Triggers                                                               */
  /* ---------------------------------------------------------------------- */

  /* a. The early hook, including anything it queued before this file ran. */
  if (window.__kopFgHook) {
    window.__kopFgHook.notify = function () { log('cart write seen by the hook'); schedule(200); };
    if (window.__kopFgHook.queue > 0) {
      log('draining', window.__kopFgHook.queue, 'queued cart writes');
      window.__kopFgHook.queue = 0;
      schedule(200);
    }
  } else {
    console.warn('[Kopstorm free gift] The head hook is missing, falling back to click and Upcart triggers only.');
  }

  /* b. Upcart. */
  function bindUpcart() {
    log('Upcart detected, subscribing');
    if (typeof window.upcartSubscribeAddedToCart === 'function') {
      window.upcartSubscribeAddedToCart(function () { schedule(200); });
    }
    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(function (event) {
        if (event && event.cart) lastCart = event.cart;
        paint();
        schedule(200);
      });
    }
    if (typeof window.upcartSubscribeItemRemoved === 'function') {
      window.upcartSubscribeItemRemoved(function () { schedule(200); });
    }
    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(function (event) {
        if (event && event.cart) lastCart = event.cart;
        paint();
        schedule(400);
      });
    }
    if (typeof window.upcartSubscribeCartOpened === 'function') {
      window.upcartSubscribeCartOpened(function () { paint(); schedule(200); });
    }
  }

  var upcartTries = 0;
  (function waitForUpcart() {
    if (typeof window.upcartSubscribeCartUpdated === 'function') { bindUpcart(); return; }
    if (upcartTries > 60) { log('no Upcart on this page'); return; }
    upcartTries += 1;
    setTimeout(waitForUpcart, 250);
  })();

  /* c. Click fallback. Cheap, and it does not care how the add was performed. */
  var ADD_HINT = /add[-_ ]?to[-_ ]?cart|addtocart|product-form|upcart|kaching|buy/i;

  function looksLikeAdd(target) {
    if (!target || !target.closest) return false;
    if (target.closest('form[action*="/cart/add"]')) return true;
    if (target.closest('[name="add"], [type="submit"], button, [role="button"], a[href*="/cart"]')) return true;
    var node = target.closest('[class], [id]');
    if (!node) return false;
    return ADD_HINT.test((node.className && node.className.baseVal) || node.className || '') || ADD_HINT.test(node.id || '');
  }

  document.addEventListener('click', function (event) {
    if (!looksLikeAdd(event.target)) return;
    passes = 0;
    setTimeout(function () { reconcile(); }, 900);
    setTimeout(function () { reconcile(); }, 2200);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    passes = 0;
    setTimeout(function () { reconcile(); }, 900);
    setTimeout(function () { reconcile(); }, 2200);
  }, true);

  /* d. Theme events, load, focus and back navigation. */
  document.addEventListener('cart:updated', function () { paint(); schedule(400); });
  document.addEventListener('cart:refresh', function () { setTimeout(paint, 300); });
  window.addEventListener('pageshow', function (event) { if (event.persisted) { passes = 0; reconcile(); } });
  window.addEventListener('focus', function () { passes = 0; schedule(600); });

  function holdsGift() {
    if (!lastCart || !lastCart.items) return false;
    return lastCart.items.some(function (line) { return GIFT[line.variant_id]; });
  }

  var observer = new MutationObserver(function () {
    if ((!DATA.labelFree && !DATA.lockLine) || !holdsGift()) return;
    clearTimeout(observer.__timer);
    observer.__timer = setTimeout(paint, 200);
  });
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                   */
  /* ---------------------------------------------------------------------- */
  var initial = { items: ((DATA.cart && DATA.cart.items) || []).map(function (line) {
    return { product_id: line.pid, variant_id: line.vid, quantity: line.q, key: line.key };
  }) };
  lastCart = initial;

  function boot() {
    paint();
    topUpCollections().then(function () {
      log('rules ready', DATA.rules.map(function (rule) { return rule.title + ' <- ' + rule.products.length + ' products'; }));
      if (differs(initial.items)) reconcile();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ---------------------------------------------------------------------- */
  /* Storefront diagnostics                                                 */
  /* ---------------------------------------------------------------------- */
  window.KopstormFreeGift = {
    data: DATA,
    reconcile: function () { passes = 0; return reconcile(); },
    reset: function () {
      store = { granted: {}, declined: {} };
      deadVariants = {};
      if (window.__kopFgHook) window.__kopFgHook.dead = deadVariants;
      writeStore(store);
      passes = 0;
      return reconcile();
    },
    debug: function (on) {
      try { sessionStorage.setItem('kopstorm:freegift:debug', on === false ? '0' : '1'); } catch (e) { /* ignore */ }
      DEBUG = on !== false;
      return DEBUG;
    },
    diagnose: function () {
      return fetchCart().then(function (cart) {
        var report = {
          mode: DATA.mode,
          max: DATA.max,
          respectRemoval: DATA.respectRemoval,
          rules: DATA.rules.map(function (rule) {
            return {
              gift: rule.title,
              giftVariant: rule.variant,
              giftPrice: rule.price,
              collection: rule.collectionTitle,
              qualifyingLoaded: rule.products.length,
              qualifyingTotal: rule.count
            };
          }),
          issues: DATA.issues || [],
          priceMode: DATA.priceMode,
          pricedGifts: (DATA.rules || []).filter(function (rule) { return rule.price > 0; })
            .map(function (rule) { return rule.title + ' at ' + (rule.price / 100).toFixed(2); }),
          cartLines: cart.items.map(function (line) {
            return {
              title: line.title,
              productId: line.product_id,
              variantId: line.variant_id,
              qty: line.quantity,
              price: line.price,
              isGift: !!GIFT[line.variant_id],
              qualifiesUnderRule: QUALIFY[line.product_id] === undefined
              ? (ALL_RULE === null ? null : ALL_RULE + 1)
              : QUALIFY[line.product_id] + 1
            };
          }),
          entitled: entitlement(cart.items),
          held: held(cart.items),
          session: store,
          upcart: typeof window.upcartSubscribeCartUpdated === 'function',
          headHook: !!window.__kopFgHook,
          lockStylesOnPage: !!document.querySelector('style[data-kop-fg-lock]'),
          shadowDom: !!(window.upcartDocumentOrShadowRoot && window.upcartDocumentOrShadowRoot.host)
        };
        console.table(report.rules);
        console.table(report.cartLines);
        console.log('[Kopstorm free gift]', report);
        if (!report.cartLines.some(function (line) { return line.qualifiesUnderRule; })) {
          console.warn('[Kopstorm free gift] Nothing in the cart matches a rule, so no gift is due. Check that the product is in the qualifying collection.');
        }
        return report;
      });
    }
  };

  log('ready', DATA.rules.length, 'rules');
})();
